import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Key, X } from "lucide-react";
import { Button } from "@finora/ui";
import { api } from "../../lib/api";

function RevealModal({ rawKey, onClose }: { rawKey: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">Sistemski API ključ</h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="space-y-3 px-5 py-5">
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Ovo je jedini put da vidiš pun ključ - sačuvaj ga sada (npr. u n8n credentials). Stari
            ključ (ako je postojao) je automatski opozvan.
          </p>
          <div className="rounded-lg border border-border bg-app-bg px-3 py-2 font-mono text-sm break-all">
            {rawKey}
          </div>
        </div>
        <div className="flex justify-end border-t border-border px-5 py-4">
          <Button onClick={onClose}>U redu</Button>
        </div>
      </div>
    </div>
  );
}

export function SystemApiKeySection() {
  const queryClient = useQueryClient();
  const [revealKey, setRevealKey] = useState<string | null>(null);

  const { data: keys, isLoading } = useQuery({
    queryKey: ["system-api-keys"],
    queryFn: () => api.listSystemApiKeys(),
  });

  const generate = useMutation({
    mutationFn: () => api.generateSystemApiKey("n8n / finora-mailpipe"),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["system-api-keys"] });
      setRevealKey(result.rawKey);
    },
  });

  const active = keys?.find((k) => !k.revokedAt);

  return (
    <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
      <h2 className="mb-1 flex items-center gap-2 text-base font-semibold text-slate-900">
        <Key className="h-4 w-4" aria-hidden />
        Sistemski API ključ (uvoz bankovnih izvoda)
      </h2>
      <p className="mb-3 text-sm text-slate-500">
        Jedan ključ za ceo sistem, koristi ga n8n/finora-mailpipe za sve firme odjednom - firma se
        prepoznaje preko uvozne email adrese (PIB_BROJRAČUNA@import...), ne preko ovog ključa.
        Generisanje novog opoziva prethodni.
      </p>

      {isLoading ? (
        <p className="text-sm text-slate-400">Učitavanje...</p>
      ) : active ? (
        <p className="mb-3 text-sm text-slate-700">
          Aktivan ključ: <span className="font-mono">{active.keyPrefix}…</span>
          {active.lastUsedAt && (
            <span className="text-slate-400">
              {" "}
              · poslednja upotreba {new Date(active.lastUsedAt).toLocaleString("sr-RS")}
            </span>
          )}
        </p>
      ) : (
        <p className="mb-3 text-sm text-amber-600">Još nema aktivnog sistemskog ključa.</p>
      )}

      <Button onClick={() => generate.mutate()} disabled={generate.isPending}>
        {active ? "Generiši novi (opoziva stari)" : "Generiši ključ"}
      </Button>

      {revealKey && <RevealModal rawKey={revealKey} onClose={() => setRevealKey(null)} />}
    </div>
  );
}
